import React, { useState } from 'react';
import { CheckCircle, Clock, AlertCircle, User, Plus, Search } from 'lucide-react';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: 'backlog' | 'todo' | 'in_progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high';
  dueDate?: string;
  assignee?: string;
  project?: string;
}

export default function KanbanBoard() {
  const [tasks, setTasks] = useState<Task[]>([
    {
      id: '1',
      title: 'Design homepage mockup',
      description: 'Create wireframe and visual design for the new homepage',
      status: 'todo',
      priority: 'high',
      dueDate: '2025-02-10',
      assignee: 'Sarah Chen',
      project: 'Website Redesign'
    },
    {
      id: '2',
      title: 'Implement user authentication',
      description: 'Set up login, registration, and password reset flows',
      status: 'in_progress',
      priority: 'high',
      dueDate: '2025-02-12',
      assignee: 'Michael Johnson',
      project: 'Mobile App Development'
    },
    {
      id: '3',
      title: 'Write content for about page',
      description: 'Create compelling copy for the about us section',
      status: 'review',
      priority: 'medium',
      dueDate: '2025-02-08',
      assignee: 'Emily Davis',
      project: 'Website Redesign'
    },
    {
      id: '4',
      title: 'Fix navigation menu bug',
      description: 'Resolve issue with dropdown menu not working on mobile',
      status: 'done',
      priority: 'high',
      dueDate: '2025-02-05',
      assignee: 'James Wilson',
      project: 'Website Redesign'
    },
    {
      id: '5',
      title: 'Create social media graphics',
      description: 'Design promotional images for Facebook and Instagram',
      status: 'in_progress',
      priority: 'medium',
      dueDate: '2025-02-15',
      assignee: 'Sarah Chen',
      project: 'Marketing Campaign'
    },
    {
      id: '6',
      title: 'Research competitors',
      description: 'Analyze top 5 competitors and create report',
      status: 'backlog',
      priority: 'low',
      dueDate: '2025-02-20',
      assignee: 'Emily Davis',
      project: 'Marketing Campaign'
    },
    {
      id: '7',
      title: 'Set up analytics',
      description: 'Implement Google Analytics and create dashboard',
      status: 'backlog',
      priority: 'medium',
      dueDate: '2025-02-18',
      assignee: 'Michael Johnson',
      project: 'Website Redesign'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-500 bg-red-50';
      case 'medium':
        return 'text-yellow-500 bg-yellow-50';
      case 'low':
        return 'text-green-500 bg-green-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'backlog':
        return 'border-gray-200 bg-gray-50';
      case 'todo':
        return 'border-blue-200 bg-blue-50';
      case 'in_progress':
        return 'border-yellow-200 bg-yellow-50';
      case 'review':
        return 'border-purple-200 bg-purple-50';
      case 'done':
        return 'border-green-200 bg-green-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const columns = [
    { id: 'backlog', title: 'Backlog' },
    { id: 'todo', title: 'To Do' },
    { id: 'in_progress', title: 'In Progress' },
    { id: 'review', title: 'Review' },
    { id: 'done', title: 'Done' }
  ];

  const filteredTasks = tasks.filter(task => 
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.project && task.project.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex space-x-2">
          <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Card</span>
          </button>
          <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
            Filter
          </button>
        </div>
        <div className="relative w-full md:w-auto">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-64"
          />
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 overflow-x-auto">
        {columns.map(column => (
          <div key={column.id} className="bg-gray-50 rounded-xl p-4 min-w-[250px]">
            <h3 className="font-medium text-gray-700 mb-4 flex items-center justify-between">
              <span>{column.title}</span>
              <span className="text-sm bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                {filteredTasks.filter(task => task.status === column.id).length}
              </span>
            </h3>
            <div className="space-y-3">
              {filteredTasks
                .filter(task => task.status === column.id)
                .map(task => (
                  <div 
                    key={task.id} 
                    className={`rounded-lg p-4 shadow-sm border-l-4 ${getStatusColor(task.status)} hover:shadow-md transition-shadow cursor-pointer`}
                  >
                    <h4 className="font-medium text-gray-900 mb-2">{task.title}</h4>
                    {task.description && (
                      <p className="text-gray-600 text-sm mb-3">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {task.project && (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                          {task.project}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-3">
                      {task.dueDate && (
                        <span className="text-xs text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                      {task.assignee && (
                        <div className="flex items-center">
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-700">
                            {task.assignee.split(' ').map(n => n[0]).join('')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              <button className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 flex items-center justify-center">
                <Plus className="w-4 h-4 mr-1" />
                Add Card
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}